import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import type { EntityFilterProps } from "@components/entity/EntityFilter/types";
import { FormikField } from "@components/forms/FormikField/FormikField";
import type { FieldPath } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import type {
    KitFieldChromeProps,
    KitFieldControlProps,
} from "./kitFieldProps.types";

type EntityFieldValue = number[] | null | undefined;

/** Paths of `Values` that hold entity ids. */
export type EntityFieldPath<Values> = FieldPath<
    Values,
    EntityFieldValue,
    number[]
>;

export type EntityFieldProps<Name extends string> = Omit<
    KitFieldControlProps<Name, HTMLButtonElement, number[]>,
    "required"
> &
    KitFieldChromeProps &
    Pick<
        EntityFilterProps,
        | "mode"
        | "mutuallyExclusive"
        | "onlyBusiness"
        | "excludeAllMockEntities"
        | "isEntityDisabled"
        | "disabledEntityTooltip"
        | "triggerButtonProps"
        | "renderTrigger"
        | "container"
    >;

/**
 * Multi-entity picker bound to a typed Formik path of entity ids. Maps the
 * ids to and from `EntityFilterCombobox`'s `{ entityIds }` filter; an empty
 * pick writes `[]`. Defaults to `mode="selection"`, so no pick shows as none
 * rather than as every entity.
 */
export const EntityField = <
    Values,
    Name extends EntityFieldPath<Values> = EntityFieldPath<Values>,
>(
    props: EntityFieldProps<Name>,
) => {
    const {
        name,
        ref,
        disabled,
        onValueChange,
        mode = "selection",
        mutuallyExclusive,
        onlyBusiness,
        excludeAllMockEntities,
        isEntityDisabled,
        disabledEntityTooltip,
        triggerButtonProps,
        renderTrigger,
        container,
    } = props;

    return (
        <FormikField<EntityFieldValue>
            name={name}
            control="button"
            {...pickChromeProps(props)}
        >
            {({ field, helpers, invalid }) => (
                <EntityFilterCombobox
                    filters={{ entityIds: field.value ?? [] }}
                    onChange={(filters) => {
                        const entityIds = filters.entityIds ?? [];
                        void helpers.setValue(entityIds);
                        onValueChange?.(entityIds);
                    }}
                    onBlur={() => void helpers.setTouched(true)}
                    ref={ref}
                    invalid={invalid}
                    disabled={disabled}
                    aria-labelledby={props["aria-labelledby"]}
                    mode={mode}
                    mutuallyExclusive={mutuallyExclusive}
                    onlyBusiness={onlyBusiness}
                    excludeAllMockEntities={excludeAllMockEntities}
                    isEntityDisabled={isEntityDisabled}
                    disabledEntityTooltip={disabledEntityTooltip}
                    triggerButtonProps={triggerButtonProps}
                    renderTrigger={renderTrigger}
                    container={container}
                />
            )}
        </FormikField>
    );
};
